import { Box, Flex, useControllableState } from '@chakra-ui/react';
import { useRef } from 'react';
import { useDisclosure, useOutsideClick } from '../../../hooks';
import {
  Icon,
  IconName,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Paragraph
} from '../../atoms';

export type SelectOptionsType = Array<{
  value: string | number;
  label: string;
  sublabel?: string;
  isDisabled?: boolean;
}>;

export type SelectOnChangeType = (value: string | number) => void;

export type SelectProps = {
  options: SelectOptionsType;
  value?: string | number;
  name?: string;
  defaultValue?: string | number;
  id?: string;
  onChange?: SelectOnChangeType;
  isDisabled?: boolean;
  isRequired?: boolean;
  matchWidth?: boolean;
  placeholder?: string;
};

// FIXME: Replace this with a custom component that follows the Listbox pattern
// A menu button pattern is not accessible as a label can not be associated with it
//
// Listbox Pattern:
// https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
//
// Possisble solutions:
// Radix (unstyled): https://www.radix-ui.com/primitives/docs/components/select
// Chakra v3 (Styled): https://chakra-ui.com/docs/components/select
export function Select({
  id,
  options,
  value,
  onChange,
  isDisabled = false,
  defaultValue,
  isRequired = false,
  matchWidth = true,
  placeholder
}: SelectProps): JSX.Element {
  const [selectedValue, setSelectedValue] = useControllableState({
    defaultValue,
    value,
    onChange
  });
  const { isOpen, onClose, onToggle } = useDisclosure();
  const handleOptionClick = (value: string | number): void => {
    setSelectedValue(value);
    onClose();
  };

  const buttonInputRef = useRef<HTMLInputElement | null>(null);

  useOutsideClick({ ref: buttonInputRef, handler: () => onClose() });
  const label = options.find((o) => o.value === selectedValue)?.label;

  return (
    <Menu matchWidth={matchWidth} autoSelect={false} isOpen={isOpen}>
      <Input
        id={id}
        data-testid={`select-${id}`}
        isRequired={isRequired}
        isDisabled={isDisabled}
        type="button"
        ref={buttonInputRef}
        as={MenuButton}
        justifyContent="space-between"
        onClick={onToggle}
        w="100%"
        minW="164px"
        alignItems="center"
        textAlign="left"
        _focusVisible={{
          outlineColor: 'palette_blue.5',
          outlineWidth: '3px',
          outlineStyle: 'solid',
          outlineOffset: 0
        }}
        _active={{
          outlineColor: 'palette_blue.5',
          outlineWidth: '3px',
          outlineStyle: 'solid',
          outlineOffset: 0
        }}
        _disabled={{
          cursor: 'default',
          color: 'palette_gray.60',
          backgroundColor: 'palette_gray.5',
          outline: 'none',
          border: 'none'
        }}
      >
        {label ? label : <Box color="palette_gray.60">{placeholder}</Box>}
        <Icon
          position="absolute"
          right="space_unit.x4"
          top="0.7rem"
          name={isOpen ? IconName.ArrowUp : IconName.ArrowDown}
          w="14px"
          color="palette_gray.100s"
        />
      </Input>

      <MenuList rootProps={{ overflow: 'scroll', maxH: '300px' }}>
        {options.map((option) => (
          <MenuItem
            data-testid={`select-${id}-option-${option.value}`}
            isDisabled={option.isDisabled ?? false}
            backgroundColor={
              option.value === selectedValue ? 'palette_blue.100' : 'white'
            }
            color={option.value === selectedValue ? 'white' : 'inherit'}
            sx={{
              '&:hover': {
                backgroundColor:
                  option.value === selectedValue
                    ? 'palette_blue.100'
                    : 'palette_blue.50',
                color:
                  option.value === selectedValue ? 'white' : 'palette_gray.100'
              }
            }}
            key={option.value}
            onClick={(e): void => {
              e.stopPropagation();
              e.preventDefault();
              handleOptionClick(option.value);
            }}
          >
            {option.sublabel ? (
              <Flex justifyContent="space-between" flexDirection="column">
                <Paragraph>{option.label}</Paragraph>
                <Paragraph size="sm" color="palette_gray.60">
                  {option.sublabel}
                </Paragraph>
              </Flex>
            ) : (
              option.label
            )}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
